import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, IconButton, Popover, cn } from '@c2fo/liquidity';
import { BellExclamationDuotoneIcon, EyeIcon, QuestionCircleIcon, TimesIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import Skeleton from '@/enterprise/components/Skeleton';
import useAggregateTakerMarketStats from '@/enterprise/data/useAggregateTakerMarketStats';
import useClosedMarkets from '@/enterprise/data/useClosedMarkets';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import { useReporting } from '@/reporting';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useExchangeRates from '@/shared/data/useExchangeRates';
import convertCurrency from '@/utils/convertCurrency';
import getActiveOfferCount from '@/utils/getActiveOfferCount';
import handleNaN from '@/utils/handleNaN';
import { useLocaleFormat } from '@/utils/useLocaleFormat';
import ClosedMarketChip from '../ClosedMarketChip';
import AcceptanceTooltip from './AcceptanceTooltip';
import { useOfferActivityFilters } from './OfferActivityFilterContext';
import { PARTICIPATION_FILTER, ACCEPTED_FILTER } from './useOfferActivityFilterLogic';

const OfferActivitySummaryComponent = () => {
  const { t } = useTranslation();
  const { asCurrency, asNumber } = useLocaleFormat();
  const { data: takerMarkets } = useTakerMarkets();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const takerMarketStats = useAggregateTakerMarketStats(takerMarkets);
  const { filters, setParticipation, setAccepted } = useOfferActivityFilters();
  const { listenToMarketStats } = useMarketStatsStatus();
  const { trackEnterpriseEvent } = useReporting();
  const { getClosedMarkets } = useClosedMarkets();
  const { hasClosedMarkets, closedMarketCount, closedInvoiceAmount } = getClosedMarkets(takerMarkets, 'eligible');
  const formattedClosedInvoiceAmount = asCurrency(closedInvoiceAmount, selectedCurrency);

  const listeningForMarketStat = listenToMarketStats(
    takerMarkets.map(({ marketUuid, takerDivisionId, takerDivisionUuid, makerOrganizationUuid }) => ({
      marketUuid,
      takerId: takerDivisionId,
      takerDivisionUuid,
      makerOrganizationUuid,
    }))
  );

  const isParticipationFilterActive = filters.participation === PARTICIPATION_FILTER.OFF;
  const isAcceptedFilterActive = filters.accepted === ACCEPTED_FILTER.NOT_ACCEPTED;
  const isParticipationCardFilterActive = filters.participationSource === 'card';
  const isAcceptedCardFilterActive = filters.acceptedSource === 'card';

  const handleToggleNotParticipatingFilter = () => {
    const wasActive = isParticipationFilterActive;

    if (isParticipationFilterActive) {
      setParticipation('');
    } else {
      setParticipation(PARTICIPATION_FILTER.OFF, 'card');
    }

    trackEnterpriseEvent('offer-activity::summary-filter::toggled', {
      filterType: 'not-participating',
      isActive: !wasActive,
      source: 'summary-card',
    });
  };

  const handleToggleNotAcceptedFilter = () => {
    const wasActive = isAcceptedFilterActive;

    if (isAcceptedFilterActive) {
      setAccepted('');
    } else {
      setAccepted(ACCEPTED_FILTER.NOT_ACCEPTED, 'card');
    }

    trackEnterpriseEvent('offer-activity::summary-filter::toggled', {
      filterType: 'not-accepted',
      isActive: !wasActive,
      source: 'summary-card',
    });
  };

  const totalAmount = takerMarketStats.byCurrency[selectedCurrency].totalInvoiceAmount;
  const totalIncludedAmount = takerMarketStats.byCurrency[selectedCurrency].eligibleInvoiceAmount;
  const totalExcludedAmount = totalAmount - totalIncludedAmount;

  const {
    totalCustomerCount,
    totalDivisionCount,
    eligibleCustomerCount,
    eligibleInvoiceCount,
    eligibleDaysPaidEarly,
    participatingAmount,
    eligibleInvoiceAmount,
    acceptedAmount,
    acceptedInvoiceCount,
    acceptedDivisionCount,
    acceptedDaysPaidEarly,
    acceptedEarn,
  } = useMemo(() => {
    const uniqueCustomers = new Set<string>();
    let eligibleCustomerCount = 0;
    let eligibleInvoiceCount = 0;
    let eligibleDpeWeightedSum = 0;
    let eligibleInvoiceAmount = 0;
    let participatingAmount = 0;
    let acceptedAmount = 0;
    let acceptedEarn = 0;
    let acceptedInvoiceCount = 0;
    let acceptedDivisionCount = 0;
    let acceptedDpeWeightedSum = 0;

    for (const tm of takerMarkets) {
      uniqueCustomers.add(tm.makerOrganizationUuid);

      const isEligible = tm.eligibleInvoiceCount + tm.awardedInvoiceCount > 0;
      const isOfferEnabled = tm.offerConfig.isEnabled === true;
      const hasClearingInvoices = tm.clearingInvoiceCount > 0;

      const eligibleInvoiceAmountConverted =
        isEligible || isOfferEnabled
          ? convertCurrency({
              amount: tm.eligibleInvoiceAmount + tm.awardedInvoiceAmount,
              exchangeRates,
              from: tm.currency,
              to: selectedCurrency,
            })
          : 0;

      if (isEligible) {
        eligibleCustomerCount++;
        eligibleInvoiceCount += tm.eligibleInvoiceCount + tm.awardedInvoiceCount;
        eligibleDpeWeightedSum += convertCurrency({
          amount: tm.eligibleDpeWeightedSum + tm.awardedDpeWeightedSum,
          exchangeRates,
          from: tm.currency,
          to: selectedCurrency,
        });
        eligibleInvoiceAmount += eligibleInvoiceAmountConverted;
      }

      if (isOfferEnabled) {
        participatingAmount += eligibleInvoiceAmountConverted;
      }

      acceptedAmount += convertCurrency({
        amount: tm.clearingInvoiceAmount,
        exchangeRates,
        from: tm.currency,
        to: selectedCurrency,
      });

      acceptedInvoiceCount += tm.clearingInvoiceCount;

      if (hasClearingInvoices) {
        acceptedDivisionCount++;
      }

      acceptedDpeWeightedSum += convertCurrency({
        amount: tm.clearingDpeWeightedSum,
        exchangeRates,
        from: tm.currency,
        to: selectedCurrency,
      });

      acceptedEarn += convertCurrency({
        amount: tm.clearingEarn,
        exchangeRates,
        from: tm.currency,
        to: selectedCurrency,
      });
    }

    return {
      totalCustomerCount: uniqueCustomers.size,
      totalDivisionCount: takerMarkets.length,
      eligibleCustomerCount,
      eligibleInvoiceCount,
      eligibleDpeWeightedSum,
      eligibleInvoiceAmount,
      eligibleDaysPaidEarly: eligibleInvoiceAmount > 0 ? eligibleDpeWeightedSum / eligibleInvoiceAmount : 0,
      participatingAmount,
      acceptedAmount,
      acceptedEarn,
      acceptedInvoiceCount,
      acceptedDivisionCount,
      acceptedDaysPaidEarly: acceptedAmount > 0 ? acceptedDpeWeightedSum / acceptedAmount : 0,
    };
  }, [takerMarkets, exchangeRates, selectedCurrency]);

  const { activeOfferCountDisplay } = useMemo(() => getActiveOfferCount(takerMarkets), [takerMarkets]);

  const notParticipatingAmount = eligibleInvoiceAmount - participatingAmount;
  const notAcceptedAmount = participatingAmount - acceptedAmount;
  const estimatedEarlyPaymentAmount = acceptedAmount - acceptedEarn;

  return (
    <IntercomDataTarget target="offerActivityOverview" className="space-y-6 rounded-xl bg-secondary-500 px-6 pb-4 pt-6">
      <div className="flex flex-col justify-between gap-x-6 gap-y-2 text-white xl:flex-row xl:items-center">
        <div>
          <div className="text-2xl">{t('core.totalEligibleAmount')}</div>
          <div className="text-sm">
            {t('core.customerWithCount', {
              count: totalCustomerCount,
              formattedCount: asNumber({ value: totalCustomerCount }),
            })}{' '}
            |{' '}
            {t('core.divisionCount', {
              count: totalDivisionCount,
              formattedCount: asNumber({ value: totalDivisionCount }),
            })}
          </div>
        </div>
        <div className="truncate font-serif text-5xl font-bold">{asCurrency(totalAmount, selectedCurrency)}</div>
      </div>
      <div className="space-y-4 rounded-xl bg-white/60 p-4">
        <div className="flex flex-col justify-between gap-x-6 gap-y-2 xl:flex-row xl:items-center">
          <div>
            <div className="text-lg font-medium">{t('core.includedAmount')}</div>
            <div className={cn('text-sm', { 'blur-sm': listeningForMarketStat })}>
              {t('core.divisionCount', {
                count: eligibleCustomerCount,
                formattedCount: asNumber({ value: eligibleCustomerCount }),
              })}{' '}
              |{' '}
              {t('core.daysPaidEarlyCount', {
                count: handleNaN(eligibleDaysPaidEarly),
                formattedCount: asNumber({
                  value: handleNaN(eligibleDaysPaidEarly),
                  decimals: 0,
                }),
              })}{' '}
              |{' '}
              {t('core.invoiceWithCount', {
                count: eligibleInvoiceCount,
                formattedCount: asNumber({ value: eligibleInvoiceCount }),
              })}
            </div>
            {hasClosedMarkets && (
              <div className={cn('mt-1', { 'blur-sm': listeningForMarketStat })}>
                <ClosedMarketChip count={closedMarketCount} invoiceAmount={formattedClosedInvoiceAmount} />
              </div>
            )}
          </div>
          <div className={cn('inline-flex items-center text-3xl font-bold', { 'blur-sm': listeningForMarketStat })}>
            {asCurrency(totalIncludedAmount, selectedCurrency)}
            <Popover>
              <Popover.Trigger className="ml-2">
                <QuestionCircleIcon className="size-5 fill-text-primary" />
              </Popover.Trigger>
              <Popover.Content arrow collisionPadding={24} side="top">
                <div className="max-w-72 space-y-2 text-center">
                  <div>
                    {totalExcludedAmount > 0
                      ? t('homepage.tooltipContent2', {
                          totalIncludedAmount: asCurrency(totalIncludedAmount, selectedCurrency),
                          totalExcludedAmount: asCurrency(totalExcludedAmount, selectedCurrency),
                        })
                      : t('homepage.tooltipContent1', {
                          totalIncludedAmount: asCurrency(totalIncludedAmount, selectedCurrency),
                        })}
                  </div>
                  {totalExcludedAmount > 0 && <div>{t('homepage.tooltipContent3')}</div>}
                </div>
              </Popover.Content>
            </Popover>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div
            className={cn('flex flex-col rounded-lg border-t-4 border-deepGray-500 bg-white p-6', {
              'opacity-38': isAcceptedCardFilterActive,
            })}
          >
            <div className={cn({ 'opacity-38': isParticipationFilterActive })}>
              <div className="text-lg font-bold text-text-secondary">{t('core.participatingAmount')}</div>
              <div className={cn('text-2xl font-bold', { 'blur-sm': listeningForMarketStat })}>
                {asCurrency(participatingAmount, selectedCurrency)}
              </div>
              {activeOfferCountDisplay > 0 ? (
                <div className={cn('mt-1', { 'blur-sm': listeningForMarketStat })}>
                  <Chip
                    className="bg-indigo-100 text-indigo-900"
                    label={t('core.activeOffers', {
                      count: activeOfferCountDisplay,
                      formattedCount: asNumber({ value: activeOfferCountDisplay }),
                    })}
                    size="xs"
                  />
                </div>
              ) : null}
            </div>
            {notParticipatingAmount > 0 && (
              <div className="mt-auto">
                <div
                  className={cn(
                    'mt-2 flex items-center justify-between gap-4 rounded-lg border border-transparent bg-blueGray-50 px-4 py-2',
                    {
                      'border-stroke': isParticipationFilterActive,
                    }
                  )}
                >
                  <div className="text-text-secondary">
                    <div className="text-sm">{t('core.notParticipating')}</div>
                    <div className={cn('font-medium', { 'blur-sm': listeningForMarketStat })}>
                      {asCurrency(notParticipatingAmount, selectedCurrency)}
                    </div>
                  </div>
                  <div>
                    <IconButton
                      className="size-8 [&>svg]:h-5 [&>svg]:w-5"
                      icon={isParticipationFilterActive ? TimesIcon : EyeIcon}
                      name="toggle-not-participating-filter"
                      size="sm"
                      variant="ancillary"
                      onClick={handleToggleNotParticipatingFilter}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className={cn('flex flex-col rounded-lg border-t-4 border-primary-500 bg-white p-6', {
              'opacity-38': isParticipationCardFilterActive,
            })}
          >
            <div className={cn('flex h-full flex-col', { 'opacity-38': isAcceptedFilterActive })}>
              <div className="text-lg font-bold text-text-secondary">{t('core.estimatedAcceptanceAmount')}</div>
              <div className={cn('text-2xl font-bold', { 'blur-sm': listeningForMarketStat })}>
                {asCurrency(acceptedAmount, selectedCurrency)}
              </div>
              <div
                className={cn('text-text-secondary', {
                  'mt-auto': notAcceptedAmount === 0,
                  'blur-sm': listeningForMarketStat,
                })}
              >
                {t('core.divisionCount', {
                  count: acceptedDivisionCount,
                  formattedCount: asNumber({ value: acceptedDivisionCount }),
                })}{' '}
                |{' '}
                {t('core.daysPaidEarlyCount', {
                  count: handleNaN(acceptedDaysPaidEarly),
                  formattedCount: asNumber({
                    value: handleNaN(acceptedDaysPaidEarly),
                    decimals: 0,
                  }),
                })}{' '}
                |{' '}
                {t('core.invoiceWithCount', {
                  count: acceptedInvoiceCount,
                  formattedCount: asNumber({ value: acceptedInvoiceCount }),
                })}
              </div>
            </div>
            {notAcceptedAmount > 0 && (
              <div className="mt-auto">
                <div
                  className={cn(
                    'mt-2 flex items-center justify-between gap-4 rounded-lg border border-transparent bg-red-50 p-2',
                    {
                      'border-red-700': isAcceptedFilterActive,
                    }
                  )}
                >
                  <AcceptanceTooltip>
                    <div className="flex flex-col gap-2 text-red-700 md:flex-row md:items-center">
                      <div>
                        <BellExclamationDuotoneIcon className="size-7 fill-red-700" />
                      </div>
                      <div className="text-left">
                        <div className="text-sm">{t('core.notAccepted')}</div>
                        <div className={cn('font-medium', { 'blur-sm': listeningForMarketStat })}>
                          {asCurrency(notAcceptedAmount, selectedCurrency)}
                        </div>
                      </div>
                    </div>
                  </AcceptanceTooltip>
                  <div>
                    <IconButton
                      className="size-8 [&>svg]:h-5 [&>svg]:w-5"
                      icon={isAcceptedFilterActive ? TimesIcon : EyeIcon}
                      name="toggle-not-accepted-filter"
                      size="sm"
                      variant="ancillary"
                      onClick={handleToggleNotAcceptedFilter}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          <div
            className={cn('flex flex-col rounded-lg border-t-4 border-lightBlue-500 bg-lightBlue-50 p-6', {
              'opacity-38': isParticipationCardFilterActive || isAcceptedCardFilterActive,
            })}
          >
            <div className="text-lg font-bold text-text-secondary">{t('core.estimatedEarlyPaymentAmount')}</div>
            <div className={cn('text-2xl font-bold', { 'blur-sm': listeningForMarketStat })}>
              {asCurrency(estimatedEarlyPaymentAmount, selectedCurrency)}
            </div>
            <div className="mt-auto">
              <div className={cn('mt-2 text-text-secondary', { 'blur-sm': listeningForMarketStat })}>
                {asCurrency(acceptedEarn, selectedCurrency)} {t('core.discount')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </IntercomDataTarget>
  );
};

const OfferActivitySummaryLoading = () => (
  <div className="space-y-6 rounded-xl bg-secondary-500 px-6 pb-4 pt-6">
    <div className="flex flex-col justify-between gap-x-6 gap-y-2 xl:flex-row xl:items-center">
      <div className="space-y-2">
        <Skeleton className="bg-white/30" height="h-7" shadow={false} />
        <Skeleton className="w-48 bg-white/20" height="h-4" shadow={false} />
      </div>
      <Skeleton className="w-64 bg-white/30" height="h-14" shadow={false} />
    </div>
    <div className="space-y-4 rounded-xl bg-white/60 p-4">
      <div className="flex flex-col justify-between gap-x-6 gap-y-2 xl:flex-row xl:items-center">
        <div className="space-y-2">
          <Skeleton height="h-6" />
          <Skeleton className="w-72" height="h-4" />
        </div>
        <Skeleton className="w-48" height="h-9" />
      </div>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="flex flex-col gap-3 rounded-lg border-t-4 border-gray-200 bg-white p-6">
            <Skeleton height="h-5" />
            <Skeleton height="h-10" />
            <Skeleton className="mt-auto" height="h-10" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const OfferActivitySummary = () => (
  <QueryBoundaries LoadingComponent={OfferActivitySummaryLoading}>
    <OfferActivitySummaryComponent />
  </QueryBoundaries>
);

export default OfferActivitySummary;
